import {
  CreateReviewRequest,
  GetProductReviewStatisticsV2Response,
  GetReviewActionsRequest,
  GetReviewActionsResponse,
  GetReviewResponse,
  ListReviewsOptions,
  ListReviewsResponse,
  ProductMetadata,
  ProductPermissions,
  ReviewPermissions,
  UpdateReviewRequest,
  UpdateReviewResponse,
} from '@lib/types/api/Review';

export const productMetadataMock: ProductMetadata = {
  type: 'upm',
  upm: {
    packageVersion: '2.1.0',
  },
};

export const makeUserProfile = (
  id: string,
  username?: string,
  name?: string
) => ({
  id: String(id),
  username: username ?? `RANDOM_${id}`,
  name: name ?? `Random User ${id}`,
  image: `https://avatars.unity.com/user-${id}.png`,
  defaultOrganization: '',
  primaryEmail: `random_${id}@test.unity3d.com`,
  primaryEmailVerified: true,
  memberSince: '2020-01-15T10:00:00Z',
  reviewCount: Math.floor(Math.random() * 50) + 1,
  helpfulVoteCount: Math.floor(Math.random() * 100),
  certifiedPublisher: Math.random() > 0.8,
  organization: Math.random() > 0.5 ? `Unity Technologies` : null,
});

export const paginationResponseMock = {
  start: 0,
  count: 10,
  total: 30,
  currentPage: 1, // This is not returned by the API but is calculated in the dataMapper
};

export const createReviewRequestMock: CreateReviewRequest = {
  rating: 5,
  title: 'Excellent particle system',
  body: 'This asset saved me weeks of work. The documentation is clear and the support is great!',
  productMetadata: productMetadataMock,
};

export const createReviewResponseMock: GetReviewResponse = {
  id: '825431',
  productId: '12345',
  userId: '67890',
  rating: 5,
  title: 'Excellent particle system',
  body: 'This asset saved me weeks of work. The documentation is clear and the support is great!',
  productMetadata: productMetadataMock,
  createdAt: '2025-01-27T10:00:00Z',
  updatedAt: '2025-01-27T10:00:00Z',
};

export const updateReviewRequestMock: UpdateReviewRequest = {
  rating: 4,
  title: 'Good particle system (updated)',
  body: 'Updated: Works well but has minor issues with documentation.',
  productMetadata: productMetadataMock,
};

export const updateReviewResponseMock: UpdateReviewResponse = {
  id: '825431',
  productId: '12345',
  userId: '67890',
  rating: 4,
  title: 'Good particle system (updated)',
  body: 'Updated: Works well but has minor issues with documentation.',
  productMetadata: productMetadataMock,
  createdAt: '2025-01-27T10:00:00Z',
  updatedAt: '2025-01-27T15:00:00Z',
};

export const listReviewsOptionsMock: ListReviewsOptions = {
  productId: '12345',
  sort: 'recent',
  count: 10,
};

export const listReviewsResponseLargeMock: ListReviewsResponse = {
  items: [
    {
      id: '825431',
      productId: '12345',
      userId: '67890',
      rating: 5,
      title: 'Excellent particle system',
      body: 'This asset saved me weeks of work. The documentation is clear and the support is great!',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-27T10:00:00Z',
      updatedAt: '2025-01-27T10:00:00Z',
      userProfile: makeUserProfile('67890'),
    },
    {
      id: '825430',
      productId: '12345',
      userId: '67891',
      rating: 4,
      title: 'Very good, but needs improvement',
      body: 'Overall a solid asset, though the documentation could be more detailed.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-26T14:30:00Z',
      updatedAt: '2025-01-26T14:30:00Z',
      userProfile: makeUserProfile('67891'),
    },
    {
      id: '825429',
      productId: '12345',
      userId: '67892',
      rating: 3,
      title: 'Okay, but has some issues',
      body: 'Works as expected but I encountered a few bugs with the UI scaling.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-25T09:15:00Z',
      updatedAt: '2025-01-25T09:15:00Z',
      userProfile: makeUserProfile('67892'),
    },
    {
      id: '825428',
      productId: '12345',
      userId: '67893',
      rating: 5,
      title: 'Incredible performance',
      body: 'The optimization on this tool is top-notch. My frame rate hasn’t dropped at all.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-24T18:45:00Z',
      updatedAt: '2025-01-24T18:45:00Z',
      userProfile: makeUserProfile('67893'),
    },
    {
      id: '825427',
      productId: '12345',
      userId: '67894',
      rating: 2,
      title: 'Difficult to integrate',
      body: 'I had a hard time getting this to work with my existing pipeline.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-23T11:20:00Z',
      updatedAt: '2025-01-23T11:20:00Z',
      userProfile: makeUserProfile('67894'),
    },
    {
      id: '825426',
      productId: '12345',
      userId: '67895',
      rating: 5,
      title: 'A must-have for developers',
      body: 'If you are looking for high-quality shaders, look no further.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-22T16:10:00Z',
      updatedAt: '2025-01-22T16:10:00Z',
      userProfile: makeUserProfile('67895'),
    },
    {
      id: '825425',
      productId: '12345',
      userId: '67896',
      rating: 4,
      title: 'Solid but pricey',
      body: 'The quality is undeniable, but it is a bit expensive compared to alternatives.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-21T13:00:00Z',
      updatedAt: '2025-01-21T13:00:00Z',
      userProfile: makeUserProfile('67896'),
    },
    {
      id: '825424',
      productId: '12345',
      userId: '67897',
      rating: 1,
      title: 'Does not work on mobile',
      body: 'Total waste of time for my project. It crashes as soon as I build for Android.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-20T08:50:00Z',
      updatedAt: '2025-01-20T08:50:00Z',
      userProfile: makeUserProfile('67897'),
    },
    {
      id: '825423',
      productId: '12345',
      userId: '67898',
      rating: 5,
      title: 'Fast support',
      body: 'Had an issue with the license, and the dev replied in less than 2 hours.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-19T15:25:00Z',
      updatedAt: '2025-01-19T15:25:00Z',
      userProfile: makeUserProfile('67898'),
    },
    {
      id: '825422',
      productId: '12345',
      userId: '67899',
      rating: 3,
      title: 'Good, but lacks features',
      body: 'It is a basic implementation. It works, but I expected more modularity.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-18T10:40:00Z',
      updatedAt: '2025-01-18T10:40:00Z',
      userProfile: makeUserProfile('67899'),
    },
    {
      id: '825421',
      productId: '12345',
      userId: '67900',
      rating: 4,
      title: 'Beautiful visuals',
      body: 'The assets look stunning in HDRP. Definitely improved my project visual quality.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-17T12:15:00Z',
      updatedAt: '2025-01-17T12:15:00Z',
      userProfile: makeUserProfile('67900'),
    },
    {
      id: '825420',
      productId: '12345',
      userId: '67901',
      rating: 5,
      title: 'Perfect for beginners',
      body: 'Very easy to setup. The tutorials provided were extremely helpful.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-16T09:00:00Z',
      updatedAt: '2025-01-16T09:00:00Z',
      userProfile: makeUserProfile('67901'),
    },
  ] as unknown as GetReviewResponse[],
  start: 0,
  count: 12,
  total: 12,
};

export const listReviewsResponseMock: ListReviewsResponse = {
  items: [
    {
      id: '825431',
      productId: '12345',
      userId: '67890',
      rating: 5,
      title: 'Excellent particle system',
      body: 'This asset saved me weeks of work. The documentation is clear and the support is great!',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-27T10:00:00Z',
      updatedAt: '2025-01-27T10:00:00Z',
      userProfile: makeUserProfile('67890'),
    },
    {
      id: '825430',
      productId: '12345',
      userId: '67891',
      rating: 4,
      title: 'Very good, but needs improvement',
      body: 'Overall a solid asset, though the documentation could be more detailed.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-26T14:30:00Z',
      updatedAt: '2025-01-26T14:30:00Z',
      userProfile: makeUserProfile('67891'),
    },
    {
      id: '825429',
      productId: '12345',
      userId: '67892',
      rating: 3,
      title: 'Okay, but has some issues',
      body: 'Works as expected but I encountered a few bugs.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-25T09:15:00Z',
      updatedAt: '2025-01-25T09:15:00Z',
      userProfile: makeUserProfile('67892'),
    },
  ] as unknown as GetReviewResponse[],
  start: 0,
  count: 3,
  total: 3,
};

export const listReviewsWithFilterMock: ListReviewsOptions = {
  productId: '12345',
  sort: 'recent',
  filterRating: '5',
  count: 10,
};

export const listReviewsFilteredResponseMock: ListReviewsResponse = {
  items: [
    {
      id: '825431',
      productId: '12345',
      userId: '67890',
      rating: 5,
      title: 'Excellent particle system',
      body: 'This asset saved me weeks of work. The documentation is clear and the support is great!',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-27T10:00:00Z',
      updatedAt: '2025-01-27T10:00:00Z',
      userProfile: makeUserProfile('67890'),
    },
  ] as unknown as GetReviewResponse[],
  start: 0,
  count: 1,
  total: 1,
};

export const reviewNotFoundErrorMock = {
  type: 'https://api.unity.com/problems/not-found',
  title: 'Review not found',
  status: 404,
  detail: 'The review with ID 999999 does not exist',
};

export const unauthorizedErrorMock = {
  type: 'https://api.unity.com/problems/forbidden',
  title: 'Unauthorized',
  status: 403,
  detail: 'You do not have permission to modify this review',
};

export const validationErrorMock = {
  type: 'https://api.unity.com/problems/bad-request',
  title: 'Invalid request',
  status: 400,
  detail: 'Rating must be between 1 and 5',
};

export const invalidSortErrorMock = {
  type: 'https://api.unity.com/problems/bad-request',
  title: 'Invalid request parameters',
  status: 400,
  detail: 'sort must be one of: recent, rating',
};

export const invalidProductIdErrorMock = {
  type: 'https://api.unity.com/problems/bad-request',
  title: 'Invalid product ID',
  status: 400,
  detail: 'Product ID must be a valid integer string',
};

export const serviceUnavailableErrorMock = {
  type: 'https://api.unity.com/problems/service-unavailable',
  title: 'Service temporarily unavailable',
  status: 503,
  detail: 'Unable to retrieve reviews at this time',
};

export const mockProductPermissionsCanSubmit: ProductPermissions = {
  canSubmitReview: true,
};

export const mockProductPermissionsCannotSubmit: ProductPermissions = {
  canSubmitReview: false,
};

export const mockOwnReviewPermissions: ReviewPermissions = {
  canEdit: true,
  canDelete: true,
  canMarkHelpful: false,
  canUnmarkHelpful: false,
  canMarkUnhelpful: false,
  canUnmarkUnhelpful: false,
  canReport: false, // Own reviews cannot be reported
};

export const mockOthersReviewPermissionsNotVoted: ReviewPermissions = {
  canEdit: false,
  canDelete: false,
  canMarkHelpful: true,
  canUnmarkHelpful: false,
  canMarkUnhelpful: true,
  canUnmarkUnhelpful: false,
  canReport: true,
};

export const mockOthersReviewPermissionsVotedHelpful: ReviewPermissions = {
  canEdit: false,
  canDelete: false,
  canMarkHelpful: false,
  canUnmarkHelpful: true,
  canMarkUnhelpful: true,
  canUnmarkUnhelpful: false,
  canReport: true,
};

export const mockOthersReviewPermissionsVotedUnhelpful: ReviewPermissions = {
  canEdit: false,
  canDelete: false,
  canMarkHelpful: true,
  canUnmarkHelpful: false,
  canMarkUnhelpful: false,
  canUnmarkUnhelpful: true,
  canReport: true,
};

export const mockGetReviewActionsRequest: GetReviewActionsRequest = {
  productId: '12345',
  reviewIds: ['825431', '825432', '825433'],
};

export const mockGetReviewActionsRequestProductOnly: GetReviewActionsRequest = {
  productId: '12345',
};

export const mockGetReviewActionsResponse: GetReviewActionsResponse = {
  product: mockProductPermissionsCanSubmit,
  reviews: {
    '825431': mockOwnReviewPermissions,
    '825432': mockOthersReviewPermissionsNotVoted,
    '825433': mockOthersReviewPermissionsVotedHelpful,
  },
  ...paginationResponseMock,
};

export const mockGetReviewActionsResponseProductOnly: GetReviewActionsResponse =
  {
    product: mockProductPermissionsCannotSubmit,
    reviews: {},
    ...paginationResponseMock,
  };

export const mockGetReviewActionsResponseUserHasReview: GetReviewActionsResponse =
  {
    product: mockProductPermissionsCannotSubmit,
    reviews: {
      '825431': mockOwnReviewPermissions,
      '825432': mockOthersReviewPermissionsVotedUnhelpful,
    },
    ...paginationResponseMock,
  };

export const mockGetReviewActionsLargeResponse: GetReviewActionsResponse = {
  product: mockProductPermissionsCanSubmit,
  reviews: {
    '825431': mockOwnReviewPermissions,
    '825432': mockOthersReviewPermissionsNotVoted,
    '825433': mockOthersReviewPermissionsVotedHelpful,
    '825434': mockOwnReviewPermissions,
    '825435': mockOthersReviewPermissionsNotVoted,
    '825436': mockOthersReviewPermissionsVotedHelpful,
    '825437': mockOwnReviewPermissions,
    '825438': mockOthersReviewPermissionsNotVoted,
    '825439': mockOthersReviewPermissionsVotedHelpful,
    '825440': mockOwnReviewPermissions,
  },
  ...paginationResponseMock,
};

export const getMockedReviewStats =
  (): GetProductReviewStatisticsV2Response => ({
    productId: '12345',
    averageRating: 4.3,
    totalReviews: 30,
    ratingBreakdown: {
      '5': 17,
      '4': 8,
      '3': 2,
      '2': 3,
      '1': 0,
    },
  });

export const listReviewsResponseWith30ItemsMock: ListReviewsResponse = {
  items: Array.from({ length: 30 }, (_, i) => {
    const id = 825431 - i;
    const userId = 67890 + i;
    const rating = ((i % 5) + 1) as 1 | 2 | 3 | 4 | 5;
    const daysAgo = i;

    return {
      id: String(id),
      productId: '12345',
      userId: String(userId),
      rating,
      title: `Review ${i + 1}: ${
        rating === 5
          ? 'Excellent product!'
          : rating === 4
            ? 'Very good!'
            : rating === 3
              ? 'Good, but could be better'
              : rating === 2
                ? 'Average product'
                : 'Not satisfied'
      }`,
      body: `This is a detailed review #${i + 1}. The product quality is ${rating === 5 ? 'exceptional' : rating >= 3 ? 'good' : 'poor'}. I've been using it for ${daysAgo + 1} days and I can confirm that ${rating >= 4 ? 'it works as advertised and exceeds expectations' : 'it meets basic requirements'}. The support team is ${rating >= 3 ? 'responsive' : 'slow'}.`,
      productMetadata: productMetadataMock,
      createdAt: new Date(
        Date.now() - daysAgo * 24 * 60 * 60 * 1000
      ).toISOString(),
      updatedAt: new Date(
        Date.now() - daysAgo * 24 * 60 * 60 * 1000
      ).toISOString(),
      userProfile: makeUserProfile(String(userId)),
    };
  }) as unknown as GetReviewResponse[],
  start: 0,
  count: 10,
  total: 30,
};

export const listReviewsResponseWithReply_CanSubmit: ListReviewsResponse = {
  items: [
    {
      id: '825431',
      productId: '12346',
      userId: '67890',
      rating: 5,
      title: 'Excellent particle system',
      body: 'This asset saved me weeks of work. The documentation is clear and the support is great!',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-27T10:00:00Z',
      updatedAt: '2025-01-27T10:00:00Z',
      userProfile: makeUserProfile('67890'),
    },
    {
      id: '825430',
      productId: '12346',
      userId: '67891',
      rating: 4,
      title: 'Very good, but needs improvement',
      body: 'Overall a solid asset, though the documentation could be more detailed.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-26T14:30:00Z',
      updatedAt: '2025-01-26T14:30:00Z',
      userProfile: makeUserProfile('67891'),
      reply: {
        answer:
          'Thank you for your feedback! We just released v2.2.0 with improved documentation and examples.',
        publishedDate: '2025-01-28T08:00:00Z',
        editedDate: undefined,
        canEdit: false,
        canDelete: false,
        canHelpfulVotes: false,
        helpfulVotes: 0,
      },
    },
    {
      id: '825429',
      productId: '12346',
      userId: '67892',
      rating: 3,
      title: 'Okay, but has some issues',
      body: 'Works as expected but I encountered a few bugs.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-25T09:15:00Z',
      updatedAt: '2025-01-25T09:15:00Z',
      userProfile: makeUserProfile('67892'),
    },
  ] as unknown as GetReviewResponse[],
  start: 0,
  count: 3,
  total: 3,
};

export const listReviewsResponseWithReply_CannotSubmit: ListReviewsResponse = {
  items: [
    {
      id: '825431',
      productId: '99999',
      userId: '67890',
      rating: 5,
      title: 'Excellent particle system',
      body: 'This asset saved me weeks of work. The documentation is clear and the support is great!',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-27T10:00:00Z',
      updatedAt: '2025-01-27T10:00:00Z',
      userProfile: makeUserProfile('67890'),
      reply: {
        answer:
          'We appreciate your kind words! Feel free to reach out if you need any assistance with integration.',
        publishedDate: '2025-01-28T09:00:00Z',
        editedDate: undefined,
        canEdit: false,
        canDelete: false,
        canHelpfulVotes: false,
        helpfulVotes: 0,
      },
    },
    {
      id: '825430',
      productId: '99999',
      userId: '67891',
      rating: 4,
      title: 'Very good, but needs improvement',
      body: 'Overall a solid asset, though the documentation could be more detailed.',
      productMetadata: productMetadataMock,
      createdAt: '2025-01-26T14:30:00Z',
      updatedAt: '2025-01-26T14:30:00Z',
      userProfile: makeUserProfile('67891'),
    },
  ] as unknown as GetReviewResponse[],
  start: 0,
  count: 2,
  total: 2,
};
